// An operator works inside a shift (TZ 7.6, STAGE_4 4.6): without an open one, the only
// thing on the screen is "Start shift" - one button - and on the way out they are asked
// to close it with a handover note. An admin is asked neither.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, Flex, Form, Input, Modal, Typography } from 'antd';
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { errorText } from '@/api/errors';
import { FullScreenSpin } from '@/components/FullScreenSpin';
import { useAuth, useCurrentUser } from '@/features/auth/authContext';
import { ru } from '@/i18n/ru';

import { OperatorShiftContext } from './operatorShiftContext';
import {
  OPERATOR_SHIFT_KEY,
  endOperatorShift,
  latestOperatorShift,
  startOperatorShift,
  type OperatorShift,
} from './shiftsApi';

const s = ru.operatorShift;

function useFailure() {
  const { message } = App.useApp();
  return (error: unknown) => {
    void message.error(
      errorText(error),
    );
  };
}

function StartShift({ onLogout }: { onLogout: () => void }) {
  const queryClient = useQueryClient();
  const user = useCurrentUser();
  const { message } = App.useApp();
  const fail = useFailure();
  const start = useMutation({
    mutationFn: startOperatorShift,
    onSuccess: async () => {
      void message.success(s.started);
      await queryClient.invalidateQueries({ queryKey: [OPERATOR_SHIFT_KEY, user.id] });
    },
    onError: fail,
  });
  return (
    <Flex align="center" justify="center" style={{ minHeight: '100vh', padding: 16 }}>
      <Card style={{ width: 420, maxWidth: '100%' }}>
        <Flex vertical gap={16}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {s.startTitle}
          </Typography.Title>
          <Typography.Text type="secondary">{s.startText}</Typography.Text>
          <Button
            type="primary"
            size="large"
            block
            autoFocus
            loading={start.isPending}
            onClick={() => {
              start.mutate();
            }}
          >
            {s.start}
          </Button>
          <Button type="link" onClick={onLogout}>
            {ru.header.logout}
          </Button>
        </Flex>
      </Card>
    </Flex>
  );
}

function EndShift({
  shift,
  open,
  onClose,
}: {
  shift: OperatorShift;
  open: boolean;
  onClose: () => void;
}) {
  const [note, setNote] = useState('');
  const { logout } = useAuth();
  const { message } = App.useApp();
  const fail = useFailure();
  const end = useMutation({
    mutationFn: () => endOperatorShift(shift.id, note.trim()),
    onSuccess: async () => {
      void message.success(s.ended);
      await logout();
    },
    onError: fail,
  });
  return (
    <Modal
      open={open}
      title={s.endTitle}
      onCancel={onClose}
      destroyOnHidden
      footer={
        <Flex justify="space-between" wrap gap={8}>
          <Button onClick={() => void logout()}>{s.logoutOnly}</Button>
          <Button
            type="primary"
            loading={end.isPending}
            onClick={() => {
              end.mutate();
            }}
          >
            {s.endAndLogout}
          </Button>
        </Flex>
      }
    >
      <Form layout="vertical">
        <Form.Item label={s.handover} htmlFor="handover-note" extra={s.handoverHint}>
          <Input.TextArea
            id="handover-note"
            autoFocus
            maxLength={2000}
            autoSize={{ minRows: 3, maxRows: 8 }}
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
            }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export function OperatorShiftGate({ children }: { children: ReactNode }) {
  const user = useCurrentUser();
  const { logout } = useAuth();
  const operator = user.role === 'operator';
  const [ending, setEnding] = useState(false);
  const shift = useQuery({
    queryKey: [OPERATOR_SHIFT_KEY, user.id],
    queryFn: ({ signal }) => latestOperatorShift(user.id, signal),
    enabled: operator,
  });
  const open = shift.data && shift.data.ended_at === null ? shift.data : null;

  const state = useMemo(
    () => ({
      requestLogout: () => {
        if (operator && open) setEnding(true);
        else void logout();
      },
    }),
    [logout, open, operator],
  );

  if (!operator) {
    return <OperatorShiftContext.Provider value={state}>{children}</OperatorShiftContext.Provider>;
  }
  if (shift.isPending) return <FullScreenSpin />;
  if (!open) return <StartShift onLogout={() => void logout()} />;
  return (
    <OperatorShiftContext.Provider value={state}>
      {children}
      <EndShift
        shift={open}
        open={ending}
        onClose={() => {
          setEnding(false);
        }}
      />
    </OperatorShiftContext.Provider>
  );
}
